// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { cartlineContainerStyles } from "@ces/App/Components/Cart/CartLineContainer";
import { color, getSystemColor, xScreen } from "@ces/XDL/Utilities";
import { css } from "styled-components";

cartlineContainerStyles.container = {
    css: css`
        display: grid;
        ${({ includeActions }: any) =>
            includeActions
                ? "grid-template-columns: 99px minmax(0, 66px) minmax(45px, 1fr) minmax(0, 165px) minmax(0, 123px) minmax(0, 149px) minmax(0, 24px);"
                : "grid-template-columns: 99px minmax(0, 66px) minmax(45px, 1fr) minmax(0, 165px) minmax(0, 123px) minmax(0, 149px);"};
        grid-column-gap: 12px;
        background: ${color("common.background")};
        &:not(:last-child) {
            border-bottom: 1px solid ${color("common.border")};
        }
        padding: 16px 12px;
        width: 100%;
        ${xScreen("lte", "sm")} {
            padding: 12px 0 17px;
        }
        @media print {
            width: 100%;
            content: "";
            display: table;
            clear: both;
            -webkit-column-break-inside: avoid;
            page-break-inside: avoid;
            break-inside: avoid;
            border-bottom: 1px solid ${getSystemColor("common.border")};
            margin-bottom: 1px; /* this is to avoid the border bottom from disappearing in the print preview for some products */
        }
    `,
};
cartlineContainerStyles.image = {
    css: css`
        grid-column: 1 / 2;
        grid-row: 1 / 5;
        ${xScreen("lte", "md")} {
            grid-row: 1 / 3;
            margin-bottom: 12px;
        }
        @media print {
            min-width: 120px;
            min-height: 120px;
            margin-bottom: 0;
            float: left;
        }
    `,
};
cartlineContainerStyles.description = {
    css: css`
        grid-column: 2 / 4;
        grid-row: 1 / 2;
        position: relative;
        padding-right: 12px;
        ${xScreen("lte", "md")} {
            grid-column: 2 / 7;
            grid-row: 1 / 3;
            padding-right: 0;
        }
        @media print {
            min-width: 75%;
            max-width: 75%;
            min-height: 120px;
            float: right;
        }
    `,
};
cartlineContainerStyles.price = {
    css: css`
        grid-column: 4 / 5;
        grid-row: 1 / 2;
        position: relative;
        padding-right: 12px;
        ${xScreen("lte", "md")} {
            grid-column: 1 / 8;
            grid-row: 3 / 4;
            padding: 12px 0;
        }
        @media print {
            min-width: 35%;
            max-width: 35%;
            min-height: 50px;
            float: left;
            padding: 0;
        }
    `,
};
cartlineContainerStyles.qty = {
    css: css`
        grid-column: 5 / 6;
        grid-row: 1 / 2;
        padding-right: 12px;
        ${xScreen("lte", "md")} {
            grid-column: 1 / 3;
            grid-row: 4 / 5;
            padding-right: 0;
        }
        @media print {
            margin-right: 0;
            min-width: 30%;
            max-width: 30%;
            min-height: 50px;
            padding-left: 12px;
            float: left;
        }
    `,
};
cartlineContainerStyles.total = {
    css: css`
        grid-column: 6 / 7;
        grid-row: 1 / 2;
        position: relative;
        padding-right: 12px;
        ${xScreen("lte", "md")} {
            grid-column: 3 / 8;
            grid-row: 4 / 5;
            padding-right: 0;
        }
        @media print {
            min-width: 35%;
            max-width: 35%;
            min-height: 50px;
            float: right;
        }
    `,
};
cartlineContainerStyles.remove = {
    css: css`
        grid-column: 7 / 8;
        grid-row: 1 / 2;
        padding-top: 7px;
        margin: 0 auto;
        ${xScreen("lte", "md")} {
            padding-top: 3px;
        }
    `,
};
cartlineContainerStyles.actions = {
    css: css`
        grid-column: 2 / 4;
        grid-row: 2 / 3;
        position: relative;
        padding-right: 12px;
        ${xScreen("lte", "md")} {
            grid-column: 1 / 8;
            grid-row: 5 / 6;
            padding-right: 0;
        }
        @media print {
            display: none;
        }
    `,
};
cartlineContainerStyles.notes = {
    css: css`
        ${({ includeActions }: any) => (includeActions ? "grid-column: 2 / 8;" : "grid-column: 2 / 7;")}
        grid-row: 4 / 5;
        position: relative;
        ${xScreen("lte", "md")} {
            grid-column: 1 / 8;
            grid-row: 6 / 7;
        }
        @media print {
            min-width: 100%;
            float: left;
        }
    `,
};
